"use client";
import { ApiMsgTypes } from "@/api/AppApi/common-interfaces/IResultStamp";
import { useGetCartQuery, useManageCartItem } from "@/api/AppApi/services/cart";
import {
  CartItemTypes,
  ICartManageItemParams_Product,
  IGetCart,
} from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { useCallback, useEffect, useState } from "react";

import {
  countFromCart,
  debounce,
  getCartId,
  getById_ItemPropsToPost_Product_Event,
} from "@/app/components/pages-components/cart/cart-functions";
import ClearCartModal from "@/app/components/pages-components/cart/all-cart-items/ClearCartModal";
import { goToCartAtom } from "@/app/components/pages-components/common/go-to-cart-pop-up/GoToCartPopUp";
import { useAtom } from "jotai";
import NotFieldErrModal from "@/app/components/common-form-builder/form-builder-helpers/NotFieldErrModal";
import { currencyAtom } from "@/hooks/currencyAtom";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import { EventType } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import useSellingMode from "@/hooks/useSellingMode";
import { ProductPreviewTypes } from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import { useBundleProduct } from "../context/BundleProductContext";
import AppToast from "@/app/components/lib/toast/AppToast";
import SvgRecycleBin from "../../product/product-box/product-box-helpers/btn-expanding-add-to-card/btn-esxpanding-types/btn-expanding-icons/SvgRecycleBin";
import SvgMinus from "../../product/product-box/product-box-helpers/btn-expanding-add-to-card/btn-esxpanding-types/btn-expanding-icons/SvgMinus";
import ExpandingQuantityInput from "../../product/product-box/product-box-helpers/btn-expanding-add-to-card/btn-esxpanding-types/ExpandingQuantityInput";
import Tooltip from "@/app/components/lib/tooltip";
import Badge from "@/app/components/lib/badge";
import SvgQuestionMark from "@/app/components/lib/SvgQuestionMark";
import SvgPlus from "../../product/product-box/product-box-helpers/btn-expanding-add-to-card/btn-esxpanding-types/btn-expanding-icons/SvgPlus";
import SvgAddToCard from "../../product/product-box/product-box-helpers/btn-expanding-add-to-card/btn-esxpanding-types/btn-expanding-icons/SvgAddToCard";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { useIsMobile } from "@/hooks/useIsMobile";
import { groupBundleItemsByBundleItemId } from "@/app/components/pages-components/product-details-page/bundle/functions";

const BundleAddToCart: React.FC = ({}) => {
  const {
    quantity,
    setQuantity,
    addedToCart,
    add_to_cart_btn_title,
    product,
    viewDetails,
    cartItemId,
    outOfStock,
    currentProductInCart,
    selectedBundleItems,
    notAllBundleItemsChildrenSelected,
  } = useBundleProduct();
  const {
    id,
    quantity_on_hand,
    min_quantity_in_cart,
    max_quantity_in_cart,
    slug,
  } = product ?? {};

  const productId = id;

  const router = useRouter();
  const [currency] = useAtom(currencyAtom);
  const { eventTrackers } = useEventTrackers();
  const { enableSells } = useSellingMode();
  const addToCartTxt = add_to_cart_btn_title;

  const maxQuantity = max_quantity_in_cart
    ? Math.min(quantity_on_hand, max_quantity_in_cart)
    : quantity_on_hand;

  const minQuantity = min_quantity_in_cart ?? 1;

  const [notFieldErr, setNotFieldErr] = useState<string[]>([]);

  const [show, setShow] = useState(false);
  const [, setOpenGoToCartPopUp] = useAtom(goToCartAtom);
  const getCart = useGetCartQuery();
  const cartItems = getCart?.data?.result?.items;

  const handleQuantity = (quantity: number) => {
    setQuantity(quantity);
  };

  const manageCartApi = useManageCartItem({
    onSuccess: (res: IGetCart) => {
      const { items, coupon } = res.result ?? {};
      if (res?.message?.type === ApiMsgTypes.success) {
        const addedItem = items.find((item) => item.inner_id === productId)!;
        eventTrackers(EventType.ADD_CART, [addedItem], {
          currency: currency?.code,
          value: addedItem.price,
          coupon: coupon?.code ?? "",
          discount: addedItem.discount,
        });
      }
    },
    onError: (error) => {
      console.log(error);
      handleQuantity(currentProductInCart.quantity);
      setNotFieldErr([error?.response?.data?.message?.text]);
    },
  });

  useEffect(() => {
    if (getCart.isLoading) return;
    handleQuantity(countFromCart(cartItems!, productId, false, cartItemId));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, getCart.isLoading]);

  const mutateQty = (qty: number) => {
    const cartItem = getById_ItemPropsToPost_Product_Event(
      cartItems!,
      productId,
      ProductPreviewTypes.bundle,
      cartItemId,
    );

    manageCartApi.mutate({
      ...cartItem,
      type: CartItemTypes.product,
      quantity: qty,
      bundle_items: (cartItem as ICartManageItemParams_Product)?.bundle_items
        ?.length
        ? (cartItem as ICartManageItemParams_Product)?.bundle_items
        : groupBundleItemsByBundleItemId(selectedBundleItems),
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceMutate = useCallback(debounce(mutateQty, 500), [
    cartItems?.length,
    cartItemId,
    productId,
    selectedBundleItems,
  ]);

  const handleQuantityChange = (qty: number) => {
    if (qty === 0) {
      setShow(true);
    } else {
      handleQuantity(qty);
      debounceMutate(qty);
    }
  };
  const handleIncreaseQty = (value: number = 1) => {
    handleQuantityChange(quantity + value);
    if (quantity === 0) {
      setOpenGoToCartPopUp(true);
    }
  };
  const handleChangeQty = (value: number) => {
    handleQuantityChange(value);
    if (quantity === 0) {
      setOpenGoToCartPopUp(true);
    }
  };
  const handleDecreaseQty = (value: number = 1) => {
    handleQuantityChange(quantity - value);
  };

  const handleRemoveFormCart = () => {
    setShow(false);
    handleQuantity(0);
  };
  const viewdetailsClick = () => {
    router.push(`/products/${slug}`);
  };

  const t = useTranslation();
  const [openToast, setOpenToast] = useState(false);
  const isMobile = useIsMobile();

  const disabled =
    outOfStock ||
    !(enableSells || viewDetails) ||
    notAllBundleItemsChildrenSelected;

  const valueToDecrease = quantity === minQuantity ? minQuantity : 1;

  const btnChildren = () => {
    return (
      <>
        {addedToCart ? (
          t.btn_added_to_cart
        ) : notAllBundleItemsChildrenSelected ? (
          t.choose_variant_for_all_bundle_items
        ) : viewDetails ? (
          t.view
        ) : outOfStock ? (
          t.out_of_stock
        ) : enableSells ? (
          <>
            <span style={{ marginInlineEnd: 4 }}>
              <SvgAddToCard small />
            </span>
            {addToCartTxt ? addToCartTxt : t?.add_to_cart}
          </>
        ) : (
          t.sell_disabled
        )}
      </>
    );
  };
  return (
    <>
      {notFieldErr.length > 0 && (
        <NotFieldErrModal errors={notFieldErr} setErrors={setNotFieldErr} />
      )}
      <ClearCartModal
        show={show}
        id={getCartId(cartItems!, productId, cartItemId)!}
        setShow={setShow}
        onOk={handleRemoveFormCart}
      />

      <>
        {!addedToCart ? (
          <button
            className={`bundle-btn-add-to-card`}
            onClick={() =>
              viewDetails ? viewdetailsClick() : handleIncreaseQty(minQuantity)
            }
            disabled={disabled}
          >
            {btnChildren()}
          </button>
        ) : viewDetails ? (
          <button
            className={`btn-expanded-h-added-to-card w`}
            onClick={viewdetailsClick}
          >
            <>
              <span style={{ marginInlineEnd: 4 }}>
                <SvgAddToCard small />
              </span>
              {t.btn_added_to_cart}
            </>
          </button>
        ) : (
          <div className="bundle-btn-add-to-card d-flex justify-content-between align-items-center">
            <div>{btnChildren()}</div>

            <div className={`bundle-btn-expanded-h-add-to-card-counter`}>
              <div
                onClick={() => quantity < maxQuantity && handleIncreaseQty()}
              >
                <SvgPlus disabled={quantity === maxQuantity} />
              </div>
              {minQuantity > 1 && minQuantity === quantity ? (
                <div
                  style={{ display: "flex", flexDirection: "column" }}
                  onClick={() => {
                    isMobile && setOpenToast(true);
                  }}
                >
                  <Tooltip
                    text={`${t.min_quantity_in_cart} ${minQuantity}`}
                    skip={isMobile}
                    paddingTop={20}
                  >
                    <Badge
                      badgeComponent={<SvgQuestionMark />}
                      position="top-right"
                    >
                      <ExpandingQuantityInput
                        quantity={quantity}
                        setQuantity={handleChangeQty}
                      />
                    </Badge>
                  </Tooltip>
                </div>
              ) : (
                <ExpandingQuantityInput
                  quantity={quantity}
                  setQuantity={handleChangeQty}
                />
              )}
              {quantity > minQuantity && (
                <div onClick={() => handleDecreaseQty(valueToDecrease)}>
                  <SvgMinus />
                </div>
              )}
              <div onClick={() => handleChangeQty(0)}>
                <SvgRecycleBin />
              </div>
            </div>
          </div>
        )}

        <AppToast
          message={`${t.min_quantity_in_cart} ${minQuantity}`}
          setShow={setOpenToast}
          show={openToast}
          paddingBottom={80}
          width="300px"
          cancelIcon={false}
          delay={3000}
        />
      </>
    </>
  );
};

export default BundleAddToCart;
